/**
 * uploadValidation — single source of truth for client-side upload
 * gate logic (UFL pack).
 *
 * Before UFL the gate was an inline `f.size > 10 * 1024 * 1024 ||
 * !VALID_EXTS.has(ext)` filter that collapsed four distinct rejection
 * paths (zero-byte / too-large / dotfile / unsupported-ext) into ONE
 * misleading toast that named only the wrong-ext case. Customer
 * feedback (2026-05-18): "I'm struggling to upload the attached doc,
 * I get no supported documents found error" — for a 10.6 MB PDF whose
 * `.pdf` extension was perfectly supported, but which busted the
 * 10 MB size cap.
 *
 * This module:
 *  - holds the canonical extension allowlist (`UPLOAD_VALID_EXTS`)
 *  - holds the canonical per-file byte cap (`UPLOAD_MAX_FILE_BYTES`)
 *  - exposes a `classifyUploadFile` function that returns either
 *    `{ ok: true }` or `{ ok: false; reason }` with a structured
 *    discriminant so the call site can name the actual cause.
 *  - exposes `describeUploadRejection` so two call sites
 *    (DocumentLibrary + FeasibilityRerunPanel) render the SAME copy
 *    for the SAME reason.
 *
 * Server-side parity: storage.rules guards `/users/{uid}/documents/**`
 * uploads with the same `UPLOAD_MAX_FILE_BYTES` cap so a request that
 * slips past the client (curl / SDK call) gets a 413 rather than
 * silently bloating tenant storage. See `storage.rules`.
 */

/**
 * Per-file upload byte cap. Raised from 10 MB → 100 MB in UFL-3
 * because site-feasibility PDFs (FEMA FIRM panels, full-res survey
 * plans, geotech reports with embedded photos, KMZ overlays with
 * high-res rasters) routinely exceed 10 MB. 100 MB still protects
 * against accidental gigabyte drops and stays well below the
 * `processUploadedFile` OCR chunking budget.
 */
export const UPLOAD_MAX_FILE_BYTES = 100 * 1024 * 1024;

/**
 * Canonical client-side extension allowlist. Mirrors the server-side
 * `contentType` allowlist in storage.rules. Keep the two in lockstep
 * — if you add `.heic` here, add `image/heic` there.
 */
export const UPLOAD_VALID_EXTS: ReadonlySet<string> = new Set<string>([
  '.pdf', '.docx', '.doc', '.txt', '.csv', '.xlsx', '.xls',
  '.md', '.json', '.rtf', '.pptx', '.ppt',
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.svg',
  '.kmz', '.kml',
  '.eml', '.msg', '.url',
  '.log', '.xml', '.html', '.htm',
  '.mp4', '.mov', '.avi', '.webm',
]);

/**
 * Structured discriminated union — the four reasons a file can be
 * rejected. The shape carries the data the UX needs to render an
 * actionable message ("X is 12 MB — limit is 100 MB" vs "X has
 * unsupported extension '.exe'").
 */
export type UploadRejectReason =
  | { kind: 'too-large'; sizeBytes: number; capBytes: number }
  | { kind: 'zero-byte' }
  | { kind: 'dotfile' }
  | { kind: 'unsupported-ext'; ext: string };

export type UploadClassification =
  | { ok: true }
  | { ok: false; reason: UploadRejectReason };

/**
 * Classify a single File against the canonical upload gate. Pure
 * function — no side effects, no I/O, safe to call N times on a
 * dropped FileList without batching cost.
 *
 * Order is significant: zero-byte → too-large → dotfile →
 * unsupported-ext. Zero-byte first so a 0-byte `.DS_Store` reports
 * as `zero-byte` not `dotfile` (zero-byte is the actionable issue).
 * Actually we want dotfile to win for `.DS_Store` because dotfiles
 * are USUALLY system metadata — re-prioritising: dotfile first so
 * the user isn't told to "re-export" `.DS_Store`. Then zero-byte,
 * then too-large, then ext.
 */
export function classifyUploadFile(file: File): UploadClassification {
  const name = file.name.toLowerCase();
  if (name.startsWith('.')) return { ok: false, reason: { kind: 'dotfile' } };
  if (file.size === 0) return { ok: false, reason: { kind: 'zero-byte' } };
  if (file.size > UPLOAD_MAX_FILE_BYTES) {
    return {
      ok: false,
      reason: { kind: 'too-large', sizeBytes: file.size, capBytes: UPLOAD_MAX_FILE_BYTES },
    };
  }
  const dotIdx = name.lastIndexOf('.');
  const ext = dotIdx >= 0 ? name.slice(dotIdx) : '';
  if (!UPLOAD_VALID_EXTS.has(ext)) {
    return { ok: false, reason: { kind: 'unsupported-ext', ext: ext || '(none)' } };
  }
  return { ok: true };
}

/**
 * Human-readable rejection copy for a single file. Pair with
 * `classifyUploadFile` at the call site:
 *
 *   const c = classifyUploadFile(f);
 *   if (!c.ok) toast.error(describeUploadRejection(f, c.reason));
 *
 * The message NAMES the specific reason and (when possible) tells
 * the user the next action — compress, re-export, rename, or split.
 * Never returns the generic "No supported documents found" string.
 */
export function describeUploadRejection(file: File, reason: UploadRejectReason): string {
  switch (reason.kind) {
    case 'too-large': {
      const sizeMb = (reason.sizeBytes / 1024 / 1024).toFixed(1);
      const capMb = (reason.capBytes / 1024 / 1024).toFixed(0);
      return `${file.name} is ${sizeMb} MB — limit is ${capMb} MB. Compress (Preview › Export › Reduce File Size) or split, then retry.`;
    }
    case 'zero-byte':
      return `${file.name} is empty (0 bytes) — re-export the file and retry.`;
    case 'dotfile':
      return `${file.name} starts with a dot — these are typically system metadata files (.DS_Store, .gitignore). Skipped.`;
    case 'unsupported-ext':
      return `${file.name} has unsupported extension "${reason.ext}". Supported: PDF, DOCX/DOC, XLSX/XLS, PPTX/PPT, TXT/MD/CSV/JSON/XML/HTML, images (JPG/PNG/GIF/BMP/TIFF/SVG), KMZ/KML, email (EML/MSG), video (MP4/MOV/AVI/WEBM).`;
  }
}

/**
 * Classify a whole FileList at once and partition into the
 * `valid` / `rejected` halves. The rejected half carries each file's
 * reason inline so the caller can group-by-kind for a clean toast UX
 * (one toast per reason, not one per file).
 */
export interface ClassifiedUpload {
  valid: File[];
  rejected: Array<{ file: File; reason: UploadRejectReason }>;
}

export function classifyUploadFileList(files: FileList | File[] | null | undefined): ClassifiedUpload {
  const out: ClassifiedUpload = { valid: [], rejected: [] };
  if (!files) return out;
  const arr = Array.from(files);
  for (const f of arr) {
    const c = classifyUploadFile(f);
    if (c.ok) out.valid.push(f);
    else out.rejected.push({ file: f, reason: c.reason });
  }
  return out;
}

/**
 * Build a toast-friendly summary of N rejections grouped by reason.
 * Returns one string per distinct reason — render each as its own
 * `toast.error` (avoids the per-file spam problem when the user
 * drops a folder of 100 .DS_Store files).
 */
export function summarizeRejections(
  rejected: Array<{ file: File; reason: UploadRejectReason }>,
): string[] {
  if (rejected.length === 0) return [];
  const groups = new Map<UploadRejectReason['kind'], Array<{ file: File; reason: UploadRejectReason }>>();
  for (const r of rejected) {
    const arr = groups.get(r.reason.kind) ?? [];
    arr.push(r);
    groups.set(r.reason.kind, arr);
  }
  const messages: string[] = [];
  for (const [kind, group] of groups) {
    if (group.length === 1) {
      messages.push(describeUploadRejection(group[0].file, group[0].reason));
    } else {
      const sample = group[0];
      messages.push(
        `${group.length} files rejected as ${kind}. First: ${describeUploadRejection(sample.file, sample.reason)}`,
      );
    }
  }
  return messages;
}
